import { useState } from "react";
import { useTheme } from "./ThemeContext";

const GuestProfileModal = ({ isOpen, onSave }) => {
  const { isDarkMode } = useTheme();
  const [guestName, setGuestName] = useState("");
  const [guestPhoto, setGuestPhoto] = useState("");
  const [photoPreview, setPhotoPreview] = useState("");
  const [error, setError] = useState("");

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError("Please select a valid image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError("Image size must be less than 5MB");
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result;
      setPhotoPreview(result);
      setGuestPhoto(result);
      setError("");
    };
    reader.readAsDataURL(file);
  };

  const handleRemovePhoto = () => {
    setGuestPhoto("");
    setPhotoPreview("");
  };

  const handleSave = () => {
    const trimmedName = guestName.trim();
    
    if (!trimmedName) {
      setError("Please enter your name");
      return;
    }

    if (trimmedName.length < 2) {
      setError("Name must be at least 2 characters");
      return;
    }

    if (trimmedName.length > 50) {
      setError("Name must be less than 50 characters");
      return;
    }

    onSave(trimmedName, guestPhoto);
    setGuestName("");
    setGuestPhoto("");
    setPhotoPreview("");
    setError("");
  };

  const handleSkip = () => {
    onSave("Guest User", "");
    setGuestName("");
    setGuestPhoto("");
    setPhotoPreview("");
    setError("");
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div 
        className={`w-full max-w-md rounded-2xl shadow-2xl ${
          isDarkMode 
            ? 'bg-slate-800 border border-slate-700' 
            : 'bg-white border border-gray-200'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`p-6 border-b ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
          <div className="flex items-center gap-3 mb-2">
            <div className={`h-12 w-12 rounded-full flex items-center justify-center ${
              isDarkMode 
                ? 'bg-gradient-to-br from-purple-600 to-pink-600' 
                : 'bg-gradient-to-br from-blue-500 to-cyan-500'
            }`}>
              <svg className="h-6 w-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
              </svg>
            </div>
            <div>
              <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Welcome! 👋
              </h2>
              <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                Tell us your name
              </p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-6">
          <div className={`mb-4 rounded-lg p-3 text-sm ${
            isDarkMode 
              ? 'bg-blue-900/20 text-blue-300 border border-blue-800/30' 
              : 'bg-blue-50 text-blue-700 border border-blue-200'
          }`}>
            <p className="font-medium mb-1">✨ Quick Setup</p>
            <p className="text-xs opacity-90">
              Enter your name and photo to personalize your experience. You can login later to save permanently.
            </p>
          </div>

          {/* Profile Photo Upload */}
          <div className="mb-4">
            <label className={`block text-sm font-semibold mb-2 ${
              isDarkMode ? 'text-slate-200' : 'text-gray-700'
            }`}>
              Profile Photo (Optional)
            </label>
            
            <div className="flex items-center gap-4">
              {/* Photo Preview */}
              <div className={`relative h-20 w-20 rounded-xl overflow-hidden border-2 ${
                isDarkMode ? 'border-slate-600 bg-slate-900' : 'border-gray-300 bg-gray-50'
              }`}>
                {photoPreview ? (
                  <>
                    <img
                      src={photoPreview}
                      alt="Preview"
                      className="h-full w-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={handleRemovePhoto}
                      className="absolute top-1 right-1 h-6 w-6 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600 transition-colors"
                      aria-label="Remove photo"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </>
                ) : (
                  <div className="h-full w-full flex items-center justify-center">
                    <svg className={`h-10 w-10 ${isDarkMode ? 'text-slate-600' : 'text-gray-400'}`} fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
                    </svg>
                  </div>
                )}
              </div>

              {/* Upload Button */}
              <div className="flex-1">
                <label
                  htmlFor="photoUpload"
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium cursor-pointer transition-all ${
                    isDarkMode
                      ? 'bg-slate-700 text-slate-200 hover:bg-slate-600'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Choose Photo
                </label>
                <input
                  id="photoUpload"
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  className="hidden"
                />
                <p className={`mt-1 text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                  JPG, PNG or GIF (Max 5MB)
                </p>
              </div>
            </div>
          </div>

          <div className="mb-4">
            <label 
              htmlFor="guestName" 
              className={`block text-sm font-semibold mb-2 ${
                isDarkMode ? 'text-slate-200' : 'text-gray-700'
              }`}
            >
              Your Name *
            </label>
            <input
              id="guestName"
              type="text"
              value={guestName}
              onChange={(e) => {
                setGuestName(e.target.value);
                setError("");
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSave();
                }
              }}
              placeholder="Enter your name"
              className={`w-full px-4 py-3 rounded-lg border-2 transition-all ${
                error
                  ? 'border-red-500 focus:border-red-600'
                  : isDarkMode
                    ? 'bg-slate-900 border-slate-600 text-white placeholder-slate-500 focus:border-purple-500'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500'
              } focus:outline-none focus:ring-2 ${
                error 
                  ? 'focus:ring-red-500/20' 
                  : isDarkMode 
                    ? 'focus:ring-purple-500/20' 
                    : 'focus:ring-blue-500/20'
              }`}
              autoFocus
            />
            {error && (
              <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {error}
              </p>
            )}
          </div>

          <div className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
            <p className="mb-1">💡 <strong>Note:</strong></p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Your details will be saved locally on this device</li>
              <li>Login to sync across devices</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className={`p-6 border-t flex gap-3 ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
          <button
            onClick={handleSkip}
            className={`flex-1 px-4 py-3 rounded-lg font-semibold transition-all ${
              isDarkMode
                ? 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Skip
          </button>
          <button
            onClick={handleSave}
            className={`flex-1 px-4 py-3 rounded-lg font-semibold text-white transition-all shadow-lg ${
              isDarkMode
                ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 shadow-purple-500/30'
                : 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 shadow-blue-500/30'
            }`}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default GuestProfileModal;
